// frontend/src/components/cohesion/EntityDrawer.tsx
// Brighella v2 (ADR-013 cross-cutting #4) — Universal entity drawer.
//
// One slide-over for every entity type. Reads cohesion.drawer; renders a
// compact summary specific to the entity type. Clicking the title opens
// the full page. Reduces forced navigation — Linear / Notion convention.
//
// Keyboard contract:
//   Escape    → close
//   Tab focus is moved into the drawer when it opens (via initial autofocus
//                on the title link)
//
// The drawer is purely presentational; data is fetched by each per-type
// subcomponent so the drawer can show a skeleton while loading without
// blocking the rest of the page.

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import {
  useCohesionStore,
  type DrawerTarget,
} from '../../store/cohesionStore'
import { useLanguage } from '../../i18n/index.jsx'
import EntityDrawerProduction  from './EntityDrawer.Production'
import EntityDrawerPerson      from './EntityDrawer.Person'
import EntityDrawerSpace       from './EntityDrawer.Space'
import EntityDrawerEvent       from './EntityDrawer.Event'
import EntityDrawerItem        from './EntityDrawer.Item'
import EntityDrawerReport      from './EntityDrawer.Report'
import './EntityDrawer.css'

type Lang = { t: (k: string) => string }

export default function EntityDrawer() {
  const drawer = useCohesionStore((s) => s.drawer)
  const close = useCohesionStore((s) => s.closeDrawer)
  const navigate = useNavigate()
  const { t } = useLanguage() as Lang

  const closeBtnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!drawer) return
    closeBtnRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawer, close])

  if (!drawer) return null

  return (
    <>
      <div
        className="entity-drawer-scrim"
        onClick={close}
        aria-hidden="true"
      />
      <div
        className="entity-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('entityDrawerTitle')}
      >
        <header className="entity-drawer-header">
          <span className="entity-drawer-kind">{t(`entityKind_${drawer.type}`)}</span>
          <button
            ref={closeBtnRef}
            type="button"
            className="entity-drawer-close"
            onClick={close}
            aria-label={t('close')}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div className="entity-drawer-body">
          <Inner drawer={drawer} navigate={navigate} closeDrawer={close} />
        </div>
      </div>
    </>
  )
}

function Inner({
  drawer,
  navigate,
  closeDrawer,
}: {
  drawer: DrawerTarget
  navigate: ReturnType<typeof useNavigate>
  closeDrawer: () => void
}) {
  switch (drawer.type) {
    case 'production': return <EntityDrawerProduction id={Number(drawer.id)} navigate={navigate} closeDrawer={closeDrawer} />
    case 'person':     return <EntityDrawerPerson     id={Number(drawer.id)} navigate={navigate} closeDrawer={closeDrawer} />
    case 'space':      return <EntityDrawerSpace      id={Number(drawer.id)} navigate={navigate} closeDrawer={closeDrawer} />
    case 'event':      return <EntityDrawerEvent      id={Number(drawer.id)} navigate={navigate} closeDrawer={closeDrawer} />
    case 'item':       return <EntityDrawerItem       id={Number(drawer.id)} navigate={navigate} closeDrawer={closeDrawer} />
    case 'report':     return <EntityDrawerReport     id={drawer.id}        navigate={navigate} closeDrawer={closeDrawer} />
    default:           return null
  }
}
